import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Lock, Mail, Sparkles, Code2, MessageSquareCode, Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import SEO, { buildBreadcrumbJsonLd } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ICONS = { Code2, MessageSquareCode, Sparkles };

const GuideDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);

  // Try fetching with the stored subscriber email (auto-unlock for returning visitors)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = localStorage.getItem('subscriber_email');
      try {
        const cat = await axios.get(`${API_URL}/api/magnets`);
        const found = (cat.data.magnets || []).find((m) => m.slug === slug);
        if (!cancelled) setMeta(found || null);
        if (!stored) { if (!cancelled) setLoading(false); return; }
        const res = await axios.get(`${API_URL}/api/magnets/${slug}`, {
          headers: { 'X-Subscriber-Email': stored },
        });
        if (!cancelled) {
          setMeta((m) => m || res.data);
          setBody(res.data.body_markdown || '');
          setUnlocked(true);
        }
      } catch {
        // 403 means the visitor isn't subscribed yet — we'll show the gate.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !value.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/magnets/${slug}/unlock`, { email: value, ref_code: (() => { try { const p = new URLSearchParams(window.location.search); return p.get('ref') || localStorage.getItem('referral_code') || undefined; } catch { return undefined; } })() });
      const m = res.data.magnet;
      setBody(m.body_markdown || '');
      setMeta((prev) => prev || m);
      setUnlocked(true);
      try { localStorage.setItem('subscriber_email', value); } catch {}
      window.dispatchEvent(new CustomEvent('subscriber-changed', { detail: { email: value } }));
      toast.success("You're in. Check your inbox for your welcome email too.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success('Guide copied to clipboard');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleDownloadMd = () => {
    const blob = new Blob([`# ${meta?.title || slug}\n\n${body}`], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="guide-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <h1 className="text-3xl font-light mb-3">Guide not found</h1>
        <button onClick={() => navigate('/guides')} className="text-gray-600 hover:text-black inline-flex items-center gap-2">
          <ArrowLeft size={18} /> Back to all guides
        </button>
      </div>
    );
  }

  const Icon = ICONS[meta.icon] || Sparkles;

  return (
    <div className="min-h-screen bg-white" data-testid="guide-detail">
      <SEO
        title={meta.title}
        description={meta.subtitle}
        path={`/guides/${slug}`}
        keywords={[meta.title, 'free AI guide', 'AI tips', 'AI tutorial']}
        type="article"
        jsonLd={buildBreadcrumbJsonLd([
          { name: 'Home', url: '/' },
          { name: 'Guides', url: '/guides' },
          { name: meta.title, url: `/guides/${slug}` },
        ])}
      />
      <div className="pt-32 pb-6 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/guides" className="inline-flex items-center gap-2 text-gray-600 hover:text-black mb-8">
            <ArrowLeft size={18} /> All guides
          </Link>
          <div className="flex items-start gap-4 mb-4">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${meta.color} text-white flex-shrink-0`}>
              <Icon size={26} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-light leading-tight tracking-tight mb-2">{meta.title}</h1>
              <p className="text-gray-600 text-base">{meta.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      {!unlocked ? (
        <section className="px-6 pb-32">
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-10 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-black text-white mb-5">
              <Lock size={20} />
            </div>
            <h2 className="text-2xl font-medium mb-3">Drop your email to unlock</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              You'll get this guide instantly + a free weekly digest of new AI ethics lessons. No spam, unsubscribe anytime.
            </p>
            <form onSubmit={handleUnlock} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="magnet-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="magnet-unlock-btn"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                <span>Unlock free</span>
              </button>
            </form>
            <p className="text-xs text-gray-400 mt-4">By subscribing you agree to receive emails from Riddhi. One click to unsubscribe.</p>
          </div>
        </section>
      ) : (
        <section className="px-6 pb-32">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-wrap gap-3 mb-6">
              <button onClick={handleCopyAll} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm" data-testid="magnet-copy-btn">
                {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                <span>{copied ? 'Copied' : 'Copy all'}</span>
              </button>
              <button onClick={handleDownloadMd} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm" data-testid="magnet-download-btn">
                <Download size={16} /> Download .md
              </button>
            </div>
            <article
              className="prose prose-lg max-w-none prose-headings:font-light prose-h2:mt-12 prose-h2:mb-4 prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-xl prose-pre:bg-gray-900 prose-pre:text-gray-50"
              data-testid="magnet-body"
            >
              <ReactMarkdown>{body}</ReactMarkdown>
            </article>
            <div className="mt-16 pt-10 border-t border-gray-200 text-center">
              <p className="text-gray-600 mb-4">Want more like this?</p>
              <Link to="/learn" className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800">
                Explore the Learn section <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const ArrowRight = ({ size = 16, ...rest }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...rest}><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
);

export default GuideDetail;
